'use client';
import { useMemo, useState } from 'react';
import { Box, Card, Stack, Typography, Button, LinearProgress } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DataTableV2 from '../../../components/DataTableV2';
import { useLanguage } from '../../../utils/i18n';

export default function TopDriverByRitase({ rawTrips = [], loading = false }) {
  const { t } = useLanguage();
  // Ubah default pageSize menjadi 10 untuk menampilkan Top 10
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [isViewAll, setIsViewAll] = useState(false);

  const processedData = useMemo(() => {
    // 1. Ekstrak data secara aman
    const safeData = Array.isArray(rawTrips) ? rawTrips : Array.isArray(rawTrips?.data) ? rawTrips.data : [];
    if (safeData.length === 0) return [];

    const grouped = {};

    safeData.forEach((trip) => {
      // 2. Ambil driver_name dari root object
      const dName = trip.driver_name || trip.driver || 'Unknown Driver';

      // 3. Hitung jumlah ritase dari panjang array details (jika kosong, fallback ke total_trips atau 1)
      const ritaseCount = Array.isArray(trip.details) ? trip.details.length : trip.total_trips || 1;

      if (!grouped[dName]) {
        grouped[dName] = {
          id: dName,
          driverName: dName,
          shift: trip.shift || 'Shift 1',
          ritase: 0,
          target: 90 // Target default/dummy
        };
      }

      // 4. Akumulasikan ritase
      grouped[dName].ritase += ritaseCount;
    });

    const result = Object.values(grouped).map((v, index) => {
      const pencapaian = Math.min(100, Math.round((v.ritase / v.target) * 100));

      return {
        ...v,
        id: `top-driver-${index}`,
        pencapaian: pencapaian
      };
    });

    // 5. Urutkan dari ritase terbanyak
    return result.sort((a, b) => b.ritase - a.ritase);
  }, [rawTrips]);

  const handleViewAll = () => {
    setIsViewAll(!isViewAll);
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      pageSize: !isViewAll ? 50 : 10 // Jika view all tampilkan 50, jika tidak tampilkan 10
    }));
  };

  // 6. Handler untuk mencegah infinite loop pada DataGrid
  const handlePaginationChange = (model) => {
    setPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) return prev;
      return model;
    });
  };

  const columns = [
    { field: 'driverName', headerName: t('dashboardAsset.driverName', 'Driver Name'), flex: 1.5, minWidth: 150 },
    { field: 'shift', headerName: t('dashboardAsset.shift', 'Shift'), flex: 1, align: 'center', headerAlign: 'center' },
    { field: 'ritase', headerName: t('dashboardAsset.ritase', 'Ritase'), flex: 1, align: 'center', headerAlign: 'center', type: 'number' },
    { field: 'target', headerName: t('dashboardAsset.target', 'Target'), flex: 1, align: 'center', headerAlign: 'center', type: 'number' },
    {
      field: 'pencapaian',
      headerName: t('dashboardAsset.achievement', 'Achievement'),
      flex: 1.5,
      minWidth: 150,
      renderCell: (params) => {
        const isTargetHit = params.row.pencapaian >= 100;
        const colorProgress = isTargetHit ? 'success' : params.row.pencapaian >= 50 ? 'primary' : 'error';

        return (
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress
                variant="determinate"
                value={params.row.pencapaian}
                color={colorProgress}
                sx={{ height: 8, borderRadius: 5, bgcolor: '#e0e0e0' }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold" color="text.secondary">
              {`${params.row.pencapaian}%`}
            </Typography>
          </Box>
        );
      }
    }
  ];

  return (
    <Card sx={{ p: 2, borderRadius: 3, boxShadow: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              bgcolor: '#485D9A',
              color: 'white',
              borderRadius: 2,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <EmojiEventsIcon />
          </Box>
          <Typography variant="h4" fontWeight="bold">
            {t('dashboardAsset.topDriverByRitase', 'Top Driver By Ritase')} (Dummy)
          </Typography>
        </Stack>
        <Button
          variant={isViewAll ? 'contained' : 'outlined'}
          size="small"
          onClick={handleViewAll}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            borderColor: '#485D9A',
            color: isViewAll ? 'white' : '#485D9A',
            bgcolor: isViewAll ? '#485D9A' : 'transparent',
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }}
        >
          {isViewAll ? t('dashboardAsset.showTop10', 'Show Top 10') : t('dashboardAsset.viewAll', 'View All')}
        </Button>
      </Stack>

      <Box
        sx={{
          '& > .MuiPaper-root': {
            height: isViewAll ? 550 : 500, // Sedikit dinaikkan height-nya untuk 10 baris
            transition: 'height 0.3s ease-in-out',
            boxShadow: 'none',
            border: 'none',
            p: 0
          }
        }}
      >
        <DataTableV2
          rows={processedData}
          columns={columns}
          loading={loading}
          hideExportButton={false}
          searchFieldOptions={[]}
          title=""
          pagination
          paginationMode="client"
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationChange}
          pageSizeOptions={[10, 25, 50]}
        />
      </Box>
    </Card>
  );
}
